/**
 * StagedNodeReviewer — bottom-center "< 1/N >" navigator for staged nodes.
 *
 * Phase 5.1 port of design/kaiva-canvas/project/components/canvas.jsx
 * StagedNodeReviewer. One reviewer for the whole canvas — replaces the
 * legacy per-node StagedNodePopup. Layout: type-colored dot · node title ·
 * separator · prev / count / next · separator · "Looks right" primary ·
 * edit / dismiss icon-buttons.
 *
 * Keyboard (active only when staged > 0):
 *   ←  /  →   navigate
 *   Enter    accept current
 *   Esc      dismiss current
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CanvasFlowNode } from '@/hooks/useProcessCanvas';
import { TYPE_META, normalizeNodeType } from '@/lib/nodeTypes';

interface StagedNodeReviewerProps {
  nodes: CanvasFlowNode[];
  onConfirm: (nodeId: string) => void;
  onEdit: (nodeId: string) => void;
  onDismiss: (nodeId: string) => void;
  onFocusNode: (nodeId: string) => void;
  /** Called whenever the currently-reviewed staged node changes (or null when none). */
  onActiveChange?: (nodeId: string | null) => void;
  /**
   * Track C.3 — when true, the reviewer's confirm/edit/dismiss buttons +
   * keyboard shortcuts are inert. Wired to canvas.isReplaying so the user
   * can't take destructive actions against a rewound state.
   */
  disabled?: boolean;
}

export function StagedNodeReviewer({
  nodes,
  onConfirm,
  onEdit,
  onDismiss,
  onFocusNode,
  onActiveChange,
  disabled = false,
}: StagedNodeReviewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const stagedNodes = nodes.filter((n) => n.data.staged);
  const count = stagedNodes.length;
  const safeIndex = count > 0 ? Math.min(currentIndex, count - 1) : 0;
  const currentNode = stagedNodes[safeIndex];
  const targetNodeId = currentNode?.id;

  // Focus the current staged node on the canvas + bubble up the active id
  // so focus-mode dimming knows which node is "active".
  useEffect(() => {
    if (!targetNodeId) {
      onActiveChange?.(null);
      return;
    }
    onActiveChange?.(targetNodeId);
    const timer = setTimeout(() => onFocusNode(targetNodeId), 100);
    return () => clearTimeout(timer);
  }, [targetNodeId, onFocusNode, onActiveChange]);

  // Reset active id when the reviewer disappears entirely.
  useEffect(() => {
    if (count === 0) onActiveChange?.(null);
  }, [count, onActiveChange]);

  const goTo = useCallback(
    (i: number) => setCurrentIndex(Math.max(0, Math.min(i, count - 1))),
    [count],
  );

  const handleConfirm = useCallback(() => {
    if (disabled) return;
    if (currentNode) onConfirm(currentNode.id);
  }, [currentNode, onConfirm, disabled]);

  const handleDismiss = useCallback(() => {
    if (disabled) return;
    if (currentNode) onDismiss(currentNode.id);
  }, [currentNode, onDismiss, disabled]);

  const handleEdit = useCallback(() => {
    if (disabled) return;
    if (currentNode) onEdit(currentNode.id);
  }, [currentNode, onEdit, disabled]);

  // Global keyboard shortcuts — active only while there are staged nodes.
  // Skips when focus is in an input/textarea so typing in the chat composer
  // doesn't trigger reviewer actions. Track C.3: disabled while replaying.
  useEffect(() => {
    if (count === 0 || disabled) return;

    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
    }

    function onKey(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goTo(safeIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          goTo(safeIndex + 1);
          break;
        case 'Enter':
          e.preventDefault();
          handleConfirm();
          break;
        case 'Escape':
          e.preventDefault();
          handleDismiss();
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, safeIndex, goTo, handleConfirm, handleDismiss, disabled]);

  if (count === 0) return null;

  const typeKey = normalizeNodeType(currentNode?.data.canvasNodeType);
  const typeColor = typeKey ? `hsl(${TYPE_META[typeKey].color})` : 'hsl(var(--muted-foreground))';

  return (
    <div
      className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 animate-[fadeIn_0.3s_ease-out]"
      onPointerDown={(e) => e.stopPropagation()}
      aria-disabled={disabled || undefined}
      style={disabled ? { opacity: 0.6 } : undefined}
    >
      <div
        className="flex items-center gap-2.5"
        style={{
          padding: '6px 8px 6px 12px',
          background: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
          borderRadius: 14,
          boxShadow: 'var(--shadow-3)',
        }}
      >
        {/* Type-colored dot + current node title */}
        <span
          aria-hidden="true"
          className="inline-block flex-shrink-0 rounded-full"
          style={{ width: 10, height: 10, background: typeColor }}
        />
        <span
          className="max-w-[220px] truncate text-[13px] font-medium"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          {currentNode?.data.label}
        </span>

        <Separator />

        {/* Navigation */}
        <div className="flex items-center gap-0.5">
          <NavBtn
            onClick={() => goTo(safeIndex - 1)}
            disabled={safeIndex <= 0}
            label="Previous proposal (←)"
          >
            <ChevronLeft className="h-3 w-3" />
          </NavBtn>
          <span
            className="min-w-[42px] text-center text-[11.5px] tabular-nums"
            style={{ color: 'hsl(var(--muted-foreground))' }}
            aria-live="polite"
            aria-atomic="true"
          >
            {safeIndex + 1} of {count}
          </span>
          <NavBtn
            onClick={() => goTo(safeIndex + 1)}
            disabled={safeIndex >= count - 1}
            label="Next proposal (→)"
          >
            <ChevronRight className="h-3 w-3" />
          </NavBtn>
        </div>

        <Separator />

        {/* Primary: Looks right (Enter) */}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={disabled}
          title={disabled ? 'Replaying — actions disabled' : 'Looks right (Enter)'}
          aria-label="Confirm proposal"
          className={cn(
            'inline-flex h-[28px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 text-[12px] font-medium transition-colors',
            'disabled:cursor-not-allowed',
          )}
          style={{
            background: 'hsl(var(--sienna))',
            color: 'hsl(var(--cream))',
            border: '1px solid hsl(var(--sienna))',
          }}
        >
          <Check className="h-3 w-3" />
          Looks right
        </button>

        {/* Secondary: edit / dismiss */}
        <div className="flex items-center gap-1">
          <NavBtn onClick={handleEdit} disabled={disabled} label="Add context" title="Add context">
            <Pencil className="h-3 w-3" />
          </NavBtn>
          <NavBtn onClick={handleDismiss} disabled={disabled} label="Dismiss (Esc)" title="Not right (Esc)">
            <X className="h-3 w-3" />
          </NavBtn>
        </div>
      </div>
    </div>
  );
}

function Separator() {
  return (
    <span
      aria-hidden="true"
      className="h-[18px] w-px flex-shrink-0"
      style={{ background: 'hsl(var(--border))' }}
    />
  );
}

function NavBtn({
  onClick,
  disabled,
  label,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title ?? label}
      className={cn(
        'inline-grid h-[26px] w-[26px] place-items-center rounded-md transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-30',
      )}
      style={{
        background: 'transparent',
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      {children}
    </button>
  );
}
